import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { StatusBadge } from "./StatusBadge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SERVICE_ORDER_STATUSES } from "@/lib/constants";

const activeStages = (flow) => (flow?.stages || []).filter((s) => s.active).sort((a, b) => a.position - b.position);

function StagePicker({ stages, value, onChange, excludeId, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder="Selecciona etapa" /></SelectTrigger>
      <SelectContent>
        {stages.filter((s) => s.id !== excludeId).map((s) => (
          <SelectItem key={s.id} value={s.id} data-testid={`${testId}-${s.id}`}>{s.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function ChangeStatusDialog({ open, onOpenChange, serviceOrder, onDone }) {
  const { session } = useAuth();
  const [flows, setFlows] = useState([]);
  const [mode, setMode] = useState("stage");
  const [target, setTarget] = useState("");
  const [flowId, setFlowId] = useState("");
  const [notes, setNotes] = useState("");
  const [publicNote, setPublicNote] = useState("");
  const [saving, setSaving] = useState(false);
  const hasFlow = Boolean(serviceOrder?.service_flow_id);
  const role = session?.user?.role_name;
  const canChangeFlow = session?.user?.is_super_admin || role === "dueno" || role === "gerente";

  useEffect(() => {
    if (!open) return;
    setTarget(""); setFlowId(""); setNotes(""); setPublicNote(""); setMode(hasFlow ? "stage" : "status");
    api.get("/service-flows", { params: { include_inactive: true } }).then(({ data }) => setFlows(data || [])).catch(() => setFlows([]));
  }, [open, hasFlow]);

  const currentFlow = flows.find((f) => f.id === serviceOrder?.service_flow_id);
  const newFlow = flows.find((f) => f.id === flowId);

  async function save() {
    setSaving(true);
    try {
      const id = serviceOrder.id;
      if (mode === "stage") await api.post(`/service-orders/${id}/stage`, { stage_id: target, notes: notes || null, public_note: publicNote || null });
      else if (mode === "flow") await api.post(`/service-orders/${id}/flow`, { service_flow_id: flowId, stage_id: target, reason: notes });
      else await api.post(`/service-orders/${id}/status`, { new_status: target, notes: notes || null, public_note: publicNote || null });
      toast.success(mode === "flow" ? "Flujo actualizado" : mode === "stage" ? "Etapa actualizada" : "Estatus actualizado");
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  const statusOptions = SERVICE_ORDER_STATUSES.filter((s) => s.value !== serviceOrder?.status && (!hasFlow || s.value === "cancelado"));
  const disabled = saving || !target || (mode === "flow" && (!flowId || !notes.trim()));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-change-status">
        <DialogHeader>
          <DialogTitle>{hasFlow ? "Cambiar etapa" : "Cambiar estatus"}</DialogTitle>
          <DialogDescription>Se registrará en la bitácora del folio {serviceOrder?.folio}.</DialogDescription>
        </DialogHeader>
        <Tabs value={mode} onValueChange={(v) => { setMode(v); setTarget(""); }}>
          <TabsList>
            {hasFlow && <TabsTrigger value="stage" data-testid="tab-stage">Etapa</TabsTrigger>}
            <TabsTrigger value="status" data-testid="tab-status">{hasFlow ? "Cancelar orden" : "Estatus"}</TabsTrigger>
            {canChangeFlow && <TabsTrigger value="flow" data-testid="tab-flow">Cambiar tipo de servicio</TabsTrigger>}
          </TabsList>
        </Tabs>
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground text-xs uppercase tracking-wider">Actual</span>
            <StatusBadge status={serviceOrder?.status} />
            {serviceOrder?.stage_name && <span className="text-sm font-medium" data-testid="dialog-current-stage">{serviceOrder.stage_name}</span>}
          </div>
          {mode === "stage" && (
            <div className="space-y-2">
              <Label>Nueva etapa · {currentFlow?.name || ""}</Label>
              <StagePicker stages={activeStages(currentFlow)} value={target} onChange={setTarget} excludeId={serviceOrder?.stage_id} testId="change-stage-select" />
            </div>
          )}
          {mode === "status" && (
            <div className="space-y-2">
              <Label>Nuevo estatus</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger data-testid="change-status-select"><SelectValue placeholder="Selecciona nuevo estatus" /></SelectTrigger>
                <SelectContent>
                  {statusOptions.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {mode === "flow" && (
            <div className="space-y-3">
              <Select value={flowId} onValueChange={(v) => { setFlowId(v); setTarget(""); }}>
                <SelectTrigger data-testid="change-flow-select"><SelectValue placeholder="Nuevo tipo de servicio" /></SelectTrigger>
                <SelectContent>
                  {flows.filter((f) => f.active && f.id !== serviceOrder?.service_flow_id).map((f) => (
                    <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {newFlow && <StagePicker stages={activeStages(newFlow)} value={target} onChange={setTarget} testId="change-flow-stage-select" />}
            </div>
          )}
          <div className="space-y-2">
            <Label>{mode === "flow" ? "Motivo (obligatorio)" : "Notas (opcional)"}</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="change-status-notes" />
          </div>
          {mode !== "flow" && (
            <div className="space-y-2">
              <Label>Nota para el cliente (opcional, se ve en su portal)</Label>
              <Textarea rows={2} maxLength={300} value={publicNote} onChange={(e) => setPublicNote(e.target.value)} data-testid="change-status-public-note" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={disabled} className="btn-tenant" data-testid="change-status-save">
            {saving ? "Guardando…" : "Registrar cambio"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
